// /admin/series/new — five-step wizard to build a complete series
// (metadata + seasons + episodes) in one form. Auto-saves to
// localStorage on every change + to `series_drafts` every 30s and on
// each step transition. Step 5 renders a real customer-facing preview
// using the actual `<VerticalPosterCard />` primitive so the admin sees
// exactly what audiences will see.
//
// State model: a single `useReducer(wizardReducer, initialState)` owns
// the form. The reducer is a thin shim — each step component pushes
// `SET_FIELD` / `ADD_ROW` / `REMOVE_ROW` events with a `path` (dotted
// or array) so we don't need a per-field action type.
//
// Persistence: `useEffect` on `state` writes to localStorage immediately
// (debounced via a microtask) and a `setInterval(30_000)` posts to
// `/api/admin/series/drafts`. Step transitions also flush a draft post.
//
// All admin labels are English-only per project convention; bilingual
// fields are clearly marked EN/TH. Customer-facing copy lives in
// `packages/i18n/src/catalog.ts`; admin copy is intentionally inline.

import { Fragment, useCallback, useEffect, useMemo, useReducer, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import {
  Button,
  Input,
  Label,
  Switch,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Badge,
  VerticalPosterCard,
  AiAssistButton,
  UploadField,
  type AiFieldKind,
  type AiFieldContext,
  type AiAssistResult,
  type TusRunner,
  type UploadFieldStreamResult,
  toast,
  cn,
} from "@bloom/ui";
import * as tus from "tus-js-client";
import {
  ArrowLeft,
  ArrowRight,
  Calendar,
  Check,
  ChevronDown,
  ChevronUp,
  CircleDot,
  Eye,
  FilmIcon,
  Loader2,
  Plus,
  Save,
  Sparkles,
  Trash2,
  Users,
  X,
} from "lucide-react";
import { CATEGORIES } from "@bloom/types";
import { useAuth } from "../auth/auth-context.tsx";
import { api, ApiError } from "../api/client.ts";

// ────────────────────────────────────────────────────────────────────────────
// Types
// ────────────────────────────────────────────────────────────────────────────

interface CastMember {
  name: string;
  role: string;
  photoUrl: string;
  /** R2 key of the cast photo, captured when uploaded so it can be rotated. */
  photoR2Key?: string;
}

interface SubtitleRow {
  language: string; // BCP-47 code, e.g. "en" / "th"
  label: string; // Human-readable label shown in the player menu
  vttUrl: string;
  r2Key?: string;
}

interface SeasonRow {
  localId: string;
  _id?: string; // server-assigned once saved
  seasonNumber: number;
  titleEn: string;
  titleTh: string;
  synopsisEn: string;
  synopsisTh: string;
  trailerStreamVideoId: string;
  releaseDate: string; // YYYY-MM-DD
}

interface EpisodeRow {
  localId: string;
  _id?: string;
  seasonLocalId: string;
  episodeNumber: number;
  titleEn: string;
  titleTh: string;
  descriptionEn: string;
  descriptionTh: string;
  streamVideoId: string;
  durationSec: number;
  thumbnailUrl: string;
  thumbnailR2Key?: string;
  subtitles: SubtitleRow[];
  episodePriceThb: number;
  /** "default" → respect series' freeEpisodeCount; "free" / "paid" → override */
  freeMode: "default" | "free" | "paid";
  releasedAt: string;
}

interface GalleryImage {
  url: string;
  role: "poster" | "hero" | "still" | "keyart" | "card" | "social" | "thumbnail";
  r2Key?: string;
  alt?: string;
}

interface SeriesMeta {
  _id: string;
  slug: string;
  titleEn: string;
  titleTh: string;
  taglineEn: string;
  taglineTh: string;
  synopsisEn: string;
  synopsisTh: string;
  posterUrl: string;
  posterR2Key?: string;
  /** Wide landscape hero image used on the series detail page header. */
  heroUrl: string;
  heroR2Key?: string;
  /** Series-level trailer uploaded to CF Stream. */
  trailerStreamVideoId: string;
  /** Mirrored into series.gallery on save for the customer detail page. */
  gallery: GalleryImage[];
  categories: string[];
  ageRating: string;
  year: number;
  country: string;
  cast: CastMember[];
  featured: boolean;
  published: boolean;
  freeEpisodeCount: number;
  bundlePriceThb: number;
  /** ISO datetime string; empty = no schedule */
  scheduledPublishAt: string;
}

interface WizardState {
  meta: SeriesMeta;
  seasons: SeasonRow[];
  episodes: EpisodeRow[];
  step: 1 | 2 | 3 | 4 | 5;
}

// ────────────────────────────────────────────────────────────────────────────
// Seeded options
// ────────────────────────────────────────────────────────────────────────────

// Seed category slugs come from the shared `@bloom/types` module so the
// admin's multi-select stays in lockstep with the customer chip rail.
const SEEDED_CATEGORIES = CATEGORIES.map((c) => c.slug);

const AGE_RATINGS = ["13+", "15+", "18+"];

function newLocalId(prefix: string): string {
  return `${prefix}-${Math.random().toString(36).slice(2, 9)}`;
}

function slugify(s: string): string {
  return s
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/[^a-z0-9\s-]/g, "")
    .trim()
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .slice(0, 80);
}

const initialState: WizardState = {
  meta: {
    _id: "",
    slug: "",
    titleEn: "",
    titleTh: "",
    taglineEn: "",
    taglineTh: "",
    synopsisEn: "",
    synopsisTh: "",
    posterUrl: "",
    heroUrl: "",
    trailerStreamVideoId: "",
    gallery: [],
    categories: [],
    ageRating: "15+",
    year: new Date().getFullYear(),
    country: "Thailand",
    cast: [],
    featured: false,
    published: false,
    freeEpisodeCount: 3,
    bundlePriceThb: 89,
    scheduledPublishAt: "",
  },
  seasons: [
    {
      localId: newLocalId("season"),
      seasonNumber: 1,
      titleEn: "",
      titleTh: "",
      synopsisEn: "",
      synopsisTh: "",
      trailerStreamVideoId: "",
      releaseDate: "",
    },
  ],
  episodes: [],
  step: 1,
};

// ────────────────────────────────────────────────────────────────────────────
// Reducer
// ────────────────────────────────────────────────────────────────────────────

type Action =
  | { type: "HYDRATE"; state: WizardState }
  | { type: "SET_META"; patch: Partial<SeriesMeta> }
  | { type: "ADD_CAST" }
  | { type: "UPDATE_CAST"; index: number; patch: Partial<CastMember> }
  | { type: "REMOVE_CAST"; index: number }
  | { type: "ADD_SEASON" }
  | { type: "UPDATE_SEASON"; localId: string; patch: Partial<SeasonRow> }
  | { type: "REMOVE_SEASON"; localId: string }
  | { type: "MOVE_SEASON"; localId: string; direction: "up" | "down" }
  | { type: "ADD_EPISODE"; seasonLocalId: string }
  | { type: "UPDATE_EPISODE"; localId: string; patch: Partial<EpisodeRow> }
  | { type: "REMOVE_EPISODE"; localId: string }
  | { type: "MOVE_EPISODE"; localId: string; direction: "up" | "down" }
  | { type: "ADD_SUBTITLE"; episodeLocalId: string; row: SubtitleRow }
  | { type: "REMOVE_SUBTITLE"; episodeLocalId: string; language: string }
  | { type: "SET_STEP"; step: WizardState["step"] }
  | {
      type: "BULK_IMPORT";
      meta?: Partial<SeriesMeta>;
      seasons?: SeasonRow[];
      episodes?: EpisodeRow[];
    };

function wizardReducer(state: WizardState, action: Action): WizardState {
  switch (action.type) {
    case "HYDRATE":
      return action.state;
    case "SET_META":
      return { ...state, meta: { ...state.meta, ...action.patch } };
    case "ADD_CAST":
      return {
        ...state,
        meta: { ...state.meta, cast: [...state.meta.cast, { name: "", role: "", photoUrl: "" }] },
      };
    case "UPDATE_CAST": {
      const next = state.meta.cast.slice();
      const existing = next[action.index];
      if (!existing) return state;
      next[action.index] = { ...existing, ...action.patch };
      return { ...state, meta: { ...state.meta, cast: next } };
    }
    case "REMOVE_CAST":
      return {
        ...state,
        meta: { ...state.meta, cast: state.meta.cast.filter((_, i) => i !== action.index) },
      };
    case "ADD_SEASON": {
      const nextNumber = (state.seasons[state.seasons.length - 1]?.seasonNumber ?? 0) + 1;
      return {
        ...state,
        seasons: [
          ...state.seasons,
          {
            localId: newLocalId("season"),
            seasonNumber: nextNumber,
            titleEn: "",
            titleTh: "",
            synopsisEn: "",
            synopsisTh: "",
            trailerStreamVideoId: "",
            releaseDate: "",
          },
        ],
      };
    }
    case "UPDATE_SEASON":
      return {
        ...state,
        seasons: state.seasons.map((s) =>
          s.localId === action.localId ? { ...s, ...action.patch } : s,
        ),
      };
    case "REMOVE_SEASON":
      return {
        ...state,
        seasons: state.seasons.filter((s) => s.localId !== action.localId),
        episodes: state.episodes.filter((e) => e.seasonLocalId !== action.localId),
      };
    case "MOVE_SEASON": {
      const idx = state.seasons.findIndex((s) => s.localId === action.localId);
      if (idx < 0) return state;
      const swap = action.direction === "up" ? idx - 1 : idx + 1;
      if (swap < 0 || swap >= state.seasons.length) return state;
      const next = state.seasons.slice();
      const a = next[idx];
      const b = next[swap];
      if (!a || !b) return state;
      next[idx] = b;
      next[swap] = a;
      // renumber after reorder
      next.forEach((s, i) => (s.seasonNumber = i + 1));
      return { ...state, seasons: next };
    }
    case "ADD_EPISODE": {
      const same = state.episodes.filter((e) => e.seasonLocalId === action.seasonLocalId);
      const nextNumber = (same[same.length - 1]?.episodeNumber ?? 0) + 1;
      return {
        ...state,
        episodes: [
          ...state.episodes,
          {
            localId: newLocalId("ep"),
            seasonLocalId: action.seasonLocalId,
            episodeNumber: nextNumber,
            titleEn: "",
            titleTh: "",
            descriptionEn: "",
            descriptionTh: "",
            streamVideoId: "",
            durationSec: 0,
            thumbnailUrl: "",
            subtitles: [],
            episodePriceThb: 9,
            freeMode: "default",
            releasedAt: "",
          },
        ],
      };
    }
    case "ADD_SUBTITLE": {
      return {
        ...state,
        episodes: state.episodes.map((e) =>
          e.localId === action.episodeLocalId
            ? {
                ...e,
                // dedupe per language — re-uploading replaces the existing row
                subtitles: [
                  ...e.subtitles.filter((s) => s.language !== action.row.language),
                  action.row,
                ],
              }
            : e,
        ),
      };
    }
    case "REMOVE_SUBTITLE": {
      return {
        ...state,
        episodes: state.episodes.map((e) =>
          e.localId === action.episodeLocalId
            ? { ...e, subtitles: e.subtitles.filter((s) => s.language !== action.language) }
            : e,
        ),
      };
    }
    case "UPDATE_EPISODE":
      return {
        ...state,
        episodes: state.episodes.map((e) =>
          e.localId === action.localId ? { ...e, ...action.patch } : e,
        ),
      };
    case "REMOVE_EPISODE":
      return { ...state, episodes: state.episodes.filter((e) => e.localId !== action.localId) };
    case "MOVE_EPISODE": {
      const ep = state.episodes.find((e) => e.localId === action.localId);
      if (!ep) return state;
      const sameSeason = state.episodes.filter((e) => e.seasonLocalId === ep.seasonLocalId);
      const idx = sameSeason.findIndex((e) => e.localId === action.localId);
      const swap = action.direction === "up" ? idx - 1 : idx + 1;
      if (swap < 0 || swap >= sameSeason.length) return state;
      const reordered = sameSeason.slice();
      const a = reordered[idx];
      const b = reordered[swap];
      if (!a || !b) return state;
      reordered[idx] = b;
      reordered[swap] = a;
      reordered.forEach((e, i) => (e.episodeNumber = i + 1));
      // splice back into the full episodes array preserving other seasons
      const rest = state.episodes.filter((e) => e.seasonLocalId !== ep.seasonLocalId);
      return { ...state, episodes: [...rest, ...reordered] };
    }
    case "SET_STEP":
      return { ...state, step: action.step };
    case "BULK_IMPORT":
      return {
        ...state,
        meta: action.meta ? { ...state.meta, ...action.meta } : state.meta,
        seasons: action.seasons ?? state.seasons,
        episodes: action.episodes ?? state.episodes,
      };
    default:
      return state;
  }
}

// ────────────────────────────────────────────────────────────────────────────
// Persistence helpers
// ────────────────────────────────────────────────────────────────────────────

function lsKey(adminUserId: string): string {
  return `bloom.wizard.draft.${adminUserId}`;
}

function relTime(d: Date): string {
  const diff = Date.now() - d.getTime();
  const s = Math.round(diff / 1000);
  if (s < 60) return `${s}s ago`;
  const m = Math.round(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.round(h / 24)}d ago`;
}

// Replace any gallery row that shares the same `role` so a re-upload swaps
// the image cleanly instead of stacking duplicates. The customer detail
// page reads the most recent `gallery[role: hero]` for its banner, etc.
function mergeGallery(existing: GalleryImage[], next: GalleryImage): GalleryImage[] {
  return [...existing.filter((g) => g.role !== next.role), next];
}

// ────────────────────────────────────────────────────────────────────────────
// Upload plumbing
// ────────────────────────────────────────────────────────────────────────────

// Pass-through wrapper around `fetch` that injects `credentials: "include"`
// so the admin session cookie travels on /api/admin/uploads/* calls.
const adminApiFetch: typeof fetch = (input, init) =>
  fetch(input, { ...init, credentials: "include" });

// tus-js-client runner — uploads to the presigned CF Stream tus endpoint,
// then polls /api/admin/uploads/stream/:uid until readyToStream so the
// caller can auto-fill durationSec on the episode row.
const adminTusRunner: TusRunner = ({ uploadUrl, uid, file, onProgress }) =>
  new Promise<UploadFieldStreamResult>((resolve, reject) => {
    const upload = new tus.Upload(file, {
      // We already have the presigned tus endpoint from the API; tus-js
      // would otherwise POST to `endpoint` to mint a new one.
      uploadUrl,
      // Stream's tus rev tops out around 50MB per PATCH; the default
      // chunkSize: Infinity would 413 on first PATCH. 50MB hits the
      // sweet spot between throughput and resumability.
      chunkSize: 50 * 1024 * 1024,
      retryDelays: [0, 1000, 3000, 5000, 10000],
      metadata: { name: file.name, filetype: file.type },
      onProgress: (sent, total) => onProgress((sent / total) * 100),
      onError: (err) => reject(err),
      onSuccess: () => {
        // Poll readiness so the wizard can populate durationSec without
        // making the admin guess. 60 attempts × 4s = 4 min ceiling — past
        // that the upload still succeeded; we just leave duration as 0
        // and the admin can either retry the poll or type it manually.
        void (async () => {
          for (let i = 0; i < 60; i++) {
            try {
              const res = await adminApiFetch(`/api/admin/uploads/stream/${uid}`, {
                credentials: "include",
              });
              if (res.ok) {
                const body = (await res.json()) as {
                  readyToStream: boolean;
                  durationSec: number;
                  state: string;
                };
                if (body.readyToStream) {
                  resolve({
                    uid,
                    meta: { durationSec: body.durationSec, state: body.state },
                  });
                  return;
                }
              }
            } catch {
              // transient — try again
            }
            await new Promise((r) => setTimeout(r, 4000));
          }
          resolve({ uid, meta: { durationSec: 0, state: "pending" } });
        })();
      },
    });
    upload.start();
  });

// ────────────────────────────────────────────────────────────────────────────
// Top-level component
// ────────────────────────────────────────────────────────────────────────────

const STEPS = [
  { n: 1, label: "Metadata", icon: FilmIcon },
  { n: 2, label: "Seasons", icon: CircleDot },
  { n: 3, label: "Episodes", icon: Users },
  { n: 4, label: "Import", icon: Sparkles },
  { n: 5, label: "Preview", icon: Eye },
] as const;

export function SeriesEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const adminId = user?.id ?? "anon";
  const reduce = useReducedMotion();

  const [state, dispatch] = useReducer(wizardReducer, initialState);
  const [showResumeBanner, setShowResumeBanner] = useState<{ updatedAt: Date } | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [lastSavedAt, setLastSavedAt] = useState<Date | null>(null);
  const initialLoadDone = useRef(false);

  // ── Hydrate: editing an existing series OR resume an unfinished draft.
  useEffect(() => {
    if (initialLoadDone.current) return;
    initialLoadDone.current = true;

    void (async () => {
      // Editing an existing series → fetch + hydrate, skip draft restore.
      if (id && id !== "new") {
        try {
          const data = await api.get<{ items: ServerSeries[] }>("/api/admin/series");
          const found = data.items.find((s) => s._id === id);
          if (found) {
            const seasonsRes = await api
              .get<{ items: ServerSeason[] }>(`/api/admin/series/${id}/seasons`)
              .catch(() => ({ items: [] as ServerSeason[] }));
            const epsRes = await api
              .get<{ items: ServerEpisode[] }>(`/api/admin/series/${id}/episodes`)
              .catch(() => ({ items: [] as ServerEpisode[] }));
            dispatch({
              type: "HYDRATE",
              state: hydrateFromServer(found, seasonsRes.items, epsRes.items),
            });
          }
          return;
        } catch (err) {
          console.error("[wizard] hydrate-existing failed", err);
          return;
        }
      }
      // New-series flow → check for an unfinished draft.
      try {
        const drafts = await api.get<{ items: ServerDraft[] }>("/api/admin/series/drafts");
        const mine = drafts.items.find((d) => !d.finalized);
        if (mine && mine.payload) {
          setShowResumeBanner({ updatedAt: new Date(mine.updatedAt) });
        }
      } catch {
        // ignore — server may be down or draft GET not wired in dev
      }
      // Also try localStorage (faster, offline-friendly).
      try {
        const raw = localStorage.getItem(lsKey(adminId));
        if (raw) {
          const parsed = JSON.parse(raw) as { state: WizardState; ts: number };
          if (parsed.ts && Date.now() - parsed.ts < 30 * 24 * 60 * 60 * 1000) {
            setShowResumeBanner((b) => b ?? { updatedAt: new Date(parsed.ts) });
          }
        }
      } catch {
        // ignore
      }
    })();
  }, [id, adminId]);

  // ── localStorage autosave on every state change (debounced via rAF).
  useEffect(() => {
    if (id && id !== "new") return; // editing existing → no draft persistence
    const handle = requestAnimationFrame(() => {
      try {
        localStorage.setItem(lsKey(adminId), JSON.stringify({ state, ts: Date.now() }));
      } catch {
        // quota errors are fine to swallow
      }
    });
    return () => cancelAnimationFrame(handle);
  }, [state, adminId, id]);

  // ── Server autosave every 30s.
  const saveDraftToServer = useCallback(
    async (markFinalized = false) => {
      if (id && id !== "new") return;
      try {
        await api.post("/api/admin/series/drafts", {
          payload: state as unknown as Record<string, unknown>,
          slug: state.meta.slug || undefined,
          finalized: markFinalized,
        });
        setLastSavedAt(new Date());
      } catch (err) {
        console.error("[wizard] draft save failed", err);
      }
    },
    [state, id],
  );

  useEffect(() => {
    if (id && id !== "new") return;
    const t = setInterval(() => void saveDraftToServer(false), 30_000);
    return () => clearInterval(t);
  }, [saveDraftToServer, id]);

  // ── Auto-generate slug from titleEn if user hasn't manually touched it.
  const slugManuallyEdited = useRef(false);
  useEffect(() => {
    if (slugManuallyEdited.current) return;
    if (!state.meta.titleEn) return;
    const next = slugify(state.meta.titleEn);
    if (next && next !== state.meta.slug) {
      dispatch({ type: "SET_META", patch: { slug: next } });
    }
  }, [state.meta.titleEn]); // eslint-disable-line react-hooks/exhaustive-deps

  const setStep = (step: WizardState["step"]) => {
    dispatch({ type: "SET_STEP", step });
    void saveDraftToServer(false);
  };

  const handleResume = () => {
    try {
      const raw = localStorage.getItem(lsKey(adminId));
      if (raw) {
        const parsed = JSON.parse(raw) as { state: WizardState };
        if (parsed.state) {
          dispatch({ type: "HYDRATE", state: parsed.state });
          setShowResumeBanner(null);
          toast.success("Draft restored");
          return;
        }
      }
      // fallback to server
      void (async () => {
        const drafts = await api.get<{ items: ServerDraft[] }>("/api/admin/series/drafts");
        const mine = drafts.items.find((d) => !d.finalized);
        if (mine?.payload) {
          dispatch({ type: "HYDRATE", state: mine.payload as unknown as WizardState });
          setShowResumeBanner(null);
          toast.success("Draft restored");
        }
      })();
    } catch (err) {
      toast.error((err as Error).message);
    }
  };

  const handleDiscardDraft = () => {
    try {
      localStorage.removeItem(lsKey(adminId));
    } catch {
      /* noop */
    }
    void api.delete("/api/admin/series/drafts").catch(() => {});
    setShowResumeBanner(null);
    toast.success("Draft discarded");
  };

  // ── Submission
  const submit = async (mode: "draft" | "publish" | "schedule") => {
    setSubmitting(true);
    try {
      if (mode === "draft") {
        await saveDraftToServer(false);
        toast.success("Draft saved");
        return;
      }
      if (!state.meta.titleEn.trim() || !state.meta.slug.trim()) {
        toast.error("Title (EN) and slug are required");
        return;
      }
      if (!state.meta.posterUrl.trim()) {
        toast.error("Poster URL is required");
        return;
      }
      const seriesId = state.meta._id || `series-${state.meta.slug}`;
      const published = mode === "publish";
      const scheduledPublishAt = mode === "schedule" ? state.meta.scheduledPublishAt || null : null;

      // Merge poster + hero into a gallery payload alongside any explicit
      // gallery edits the wizard accumulated. The server's `gallery` field
      // is the authoritative source for the customer detail page's hero +
      // poster variants — pushing them here keeps the two in sync.
      const galleryPayload: GalleryImage[] = [];
      const seenRoles = new Set<string>();
      for (const g of state.meta.gallery) {
        if (!g.url) continue;
        galleryPayload.push(g);
        seenRoles.add(g.role);
      }
      if (state.meta.posterUrl && !seenRoles.has("poster")) {
        galleryPayload.push({
          url: state.meta.posterUrl,
          role: "poster",
          r2Key: state.meta.posterR2Key,
        });
      }
      if (state.meta.heroUrl && !seenRoles.has("hero")) {
        galleryPayload.push({
          url: state.meta.heroUrl,
          role: "hero",
          r2Key: state.meta.heroR2Key,
        });
      }

      await api.post("/api/admin/series", {
        _id: seriesId,
        slug: state.meta.slug,
        titleEn: state.meta.titleEn,
        titleTh: state.meta.titleTh || state.meta.titleEn,
        taglineEn: state.meta.taglineEn || undefined,
        taglineTh: state.meta.taglineTh || undefined,
        synopsisEn: state.meta.synopsisEn,
        synopsisTh: state.meta.synopsisTh || state.meta.synopsisEn,
        posterUrl: state.meta.posterUrl,
        gallery: galleryPayload.length > 0 ? galleryPayload : undefined,
        trailerStreamVideoId: state.meta.trailerStreamVideoId || undefined,
        categories: state.meta.categories,
        featured: state.meta.featured,
        published,
        bundlePriceThb: state.meta.bundlePriceThb,
        freeEpisodeCount: state.meta.freeEpisodeCount,
        ageRating: state.meta.ageRating,
        year: state.meta.year,
        country: state.meta.country,
        cast: state.meta.cast
          .filter((c) => c.name.trim())
          .map((c) => ({
            name: c.name,
            role: c.role || undefined,
            photoUrl: c.photoUrl || undefined,
          })),
        scheduledPublishAt,
      });

      // Upsert seasons → episodes
      const localToServerSeason = new Map<string, string>();
      for (const season of state.seasons) {
        const seasonId = season._id || `${seriesId}-s${season.seasonNumber}`;
        await api.post(`/api/admin/series/${seriesId}/seasons`, {
          _id: seasonId,
          seasonNumber: season.seasonNumber,
          titleEn: season.titleEn || undefined,
          titleTh: season.titleTh || undefined,
          synopsisEn: season.synopsisEn || undefined,
          synopsisTh: season.synopsisTh || undefined,
          trailerStreamVideoId: season.trailerStreamVideoId || undefined,
          releaseDate: season.releaseDate || undefined,
        });
        localToServerSeason.set(season.localId, seasonId);
      }
      for (const ep of state.episodes) {
        const seasonNumber =
          state.seasons.find((s) => s.localId === ep.seasonLocalId)?.seasonNumber ?? 1;
        const epId = ep._id || `${seriesId}-s${seasonNumber}-e${ep.episodeNumber}`;
        await api.post(`/api/admin/series/${seriesId}/episodes`, {
          _id: epId,
          seasonNumber,
          episodeNumber: ep.episodeNumber,
          titleEn: ep.titleEn,
          titleTh: ep.titleTh || ep.titleEn,
          descriptionEn: ep.descriptionEn || undefined,
          descriptionTh: ep.descriptionTh || undefined,
          // Now sourced from the CF Stream tus upload in step 3 — empty
          // string is still accepted (zod default) for episodes that the
          // admin hasn't uploaded video for yet.
          streamVideoId: ep.streamVideoId,
          durationSec: ep.durationSec,
          thumbnailUrl: ep.thumbnailUrl || state.meta.posterUrl,
          subtitles:
            ep.subtitles.length > 0
              ? ep.subtitles.map((s) => ({
                  language: s.language,
                  label: s.label,
                  vttUrl: s.vttUrl,
                  default: s.language === "en",
                }))
              : undefined,
          episodePriceThb: ep.episodePriceThb,
          isFreeOverride: ep.freeMode === "default" ? null : ep.freeMode === "free" ? true : false,
          published,
          releasedAt: ep.releasedAt || undefined,
        });
      }

      // Mark draft finalized + clear localStorage.
      await saveDraftToServer(true);
      try {
        localStorage.removeItem(lsKey(adminId));
      } catch {
        /* noop */
      }
      toast.success(mode === "publish" ? "Published" : mode === "schedule" ? "Scheduled" : "Saved");
      navigate(`/series`);
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : (err as Error).message;
      toast.error(msg || "Save failed");
    } finally {
      setSubmitting(false);
    }
  };

  // ── Render
  const stepVariants = reduce
    ? { initial: { opacity: 1 }, animate: { opacity: 1 }, exit: { opacity: 1 } }
    : {
        initial: { opacity: 0, y: 12 },
        animate: { opacity: 1, y: 0 },
        exit: { opacity: 0, y: -8 },
      };

  return (
    <div className="mx-auto max-w-6xl space-y-6">
      <header className="flex items-baseline justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold">
            {id === "new" || !id ? "New series" : state.meta.titleEn || "Untitled"}
          </h1>
          <p className="text-sm text-[var(--brand-muted)]">
            Five steps. Auto-saves as you go.
            {lastSavedAt ? (
              <span className="ml-2 inline-flex items-center gap-1 text-[var(--brand-rose)]">
                <Check className="h-3 w-3" aria-hidden /> saved {relTime(lastSavedAt)}
              </span>
            ) : null}
          </p>
        </div>
        <Link
          to="/series"
          className="text-sm font-semibold text-[var(--brand-muted)] hover:text-[var(--brand-ink)]"
        >
          Cancel
        </Link>
      </header>

      {showResumeBanner ? (
        <div className="flex items-center justify-between gap-3 rounded-2xl border border-[var(--brand-pink)] bg-[var(--brand-pink-soft)] p-4">
          <div className="flex items-center gap-3">
            <Sparkles className="h-5 w-5 text-[var(--brand-rose)]" aria-hidden />
            <div>
              <p className="text-sm font-semibold">Unfinished draft</p>
              <p className="text-xs text-[var(--brand-muted)]">
                Last edited {relTime(showResumeBanner.updatedAt)}
              </p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button variant="rose" size="sm" onClick={handleResume}>
              Resume
            </Button>
            <Button variant="ghost" size="sm" onClick={handleDiscardDraft}>
              Discard
            </Button>
          </div>
        </div>
      ) : null}

      <StepIndicator step={state.step} onJump={setStep} />

      <div className="grid gap-6 lg:grid-cols-[1fr_280px]">
        <div className="min-w-0">
          <AnimatePresence mode="wait">
            <motion.div
              key={state.step}
              variants={stepVariants}
              initial="initial"
              animate="animate"
              exit="exit"
              transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
            >
              {state.step === 1 ? (
                <Step1Metadata
                  state={state}
                  dispatch={dispatch}
                  onSlugManual={() => (slugManuallyEdited.current = true)}
                />
              ) : null}
              {state.step === 2 ? <Step2Seasons state={state} dispatch={dispatch} /> : null}
              {state.step === 3 ? <Step3Episodes state={state} dispatch={dispatch} /> : null}
              {state.step === 4 ? <Step4Import state={state} dispatch={dispatch} /> : null}
              {state.step === 5 ? <Step5Preview state={state} /> : null}
            </motion.div>
          </AnimatePresence>

          <nav className="mt-6 flex items-center justify-between">
            <Button
              variant="ghost"
              onClick={() => setStep(Math.max(1, state.step - 1) as WizardState["step"])}
              disabled={state.step === 1}
            >
              <ArrowLeft className="h-4 w-4" aria-hidden /> Back
            </Button>
            {state.step < 5 ? (
              <Button
                variant="primary"
                onClick={() => setStep((state.step + 1) as WizardState["step"])}
              >
                Next <ArrowRight className="h-4 w-4" aria-hidden />
              </Button>
            ) : (
              <div className="flex flex-wrap items-center gap-2">
                <Button variant="ghost" onClick={() => submit("draft")} disabled={submitting}>
                  <Save className="h-4 w-4" aria-hidden />
                  Save draft
                </Button>
                <Button
                  variant="outline"
                  onClick={() => submit("schedule")}
                  disabled={submitting || !state.meta.scheduledPublishAt}
                  title={
                    !state.meta.scheduledPublishAt
                      ? "Set a scheduled-release datetime in step 1"
                      : undefined
                  }
                >
                  <Calendar className="h-4 w-4" aria-hidden />
                  Schedule
                </Button>
                <Button variant="rose" onClick={() => submit("publish")} disabled={submitting}>
                  {submitting ? (
                    <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                  ) : (
                    <Check className="h-4 w-4" aria-hidden />
                  )}
                  Publish now
                </Button>
              </div>
            )}
          </nav>
        </div>

        {/* Live preview rail — desktop only. Hidden on mobile. */}
        <aside className="hidden lg:block">
          <div className="sticky top-6 space-y-3">
            <p className="text-xs font-semibold uppercase tracking-wide text-[var(--brand-muted)]">
              Live preview
            </p>
            <div className="w-[200px]">
              <VerticalPosterCard
                href="#"
                titleEn={state.meta.titleEn || "Untitled"}
                titleTh={state.meta.titleTh || state.meta.titleEn || "Untitled"}
                taglineEn={state.meta.taglineEn || null}
                taglineTh={state.meta.taglineTh || null}
                posterUrl={
                  state.meta.posterUrl ||
                  "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 9 16'%3E%3Crect width='9' height='16' fill='%23f7c8d5'/%3E%3C/svg%3E"
                }
                freeEpisodeCount={state.meta.freeEpisodeCount}
                locale="en"
              />
            </div>
            <p className="text-[11px] text-[var(--brand-muted)]">
              How the card appears on the customer site.
            </p>
          </div>
        </aside>
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Step indicator — film-strip style
// ────────────────────────────────────────────────────────────────────────────

function StepIndicator({
  step,
  onJump,
}: {
  step: WizardState["step"];
  onJump: (n: WizardState["step"]) => void;
}) {
  return (
    <ol className="flex items-stretch gap-2 overflow-x-auto pb-1">
      {STEPS.map((s, i) => {
        const Icon = s.icon;
        const active = s.n === step;
        const done = s.n < step;
        return (
          <Fragment key={s.n}>
            <li>
              <button
                type="button"
                onClick={() => onJump(s.n as WizardState["step"])}
                className={cn(
                  "group relative flex h-20 w-32 shrink-0 flex-col items-start justify-between rounded-2xl border-2 px-3 py-2 text-left transition-all",
                  active
                    ? "border-[var(--brand-rose)] bg-[var(--brand-pink-soft)] shadow-[0_8px_24px_-12px_rgba(233,75,122,0.55)]"
                    : done
                      ? "border-[var(--brand-pink)] bg-white"
                      : "border-[var(--brand-border)] bg-white/60",
                )}
              >
                <div className="flex w-full items-center justify-between">
                  <span
                    className={cn(
                      "inline-flex h-6 w-6 items-center justify-center rounded-full text-[10px] font-bold",
                      active
                        ? "bg-[var(--brand-rose)] text-white"
                        : done
                          ? "bg-[var(--brand-ink)] text-white"
                          : "bg-[var(--brand-border)] text-[var(--brand-muted)]",
                    )}
                  >
                    {done ? <Check className="h-3 w-3" aria-hidden /> : s.n}
                  </span>
                  <Icon
                    className={cn(
                      "h-4 w-4",
                      active ? "text-[var(--brand-rose)]" : "text-[var(--brand-muted)]",
                    )}
                    aria-hidden
                  />
                </div>
                <span
                  className={cn(
                    "text-xs font-semibold",
                    active ? "text-[var(--brand-ink)]" : "text-[var(--brand-muted)]",
                  )}
                >
                  {s.label}
                </span>
                {active ? (
                  <span
                    aria-hidden
                    className="pointer-events-none absolute inset-x-3 -bottom-0.5 h-[3px] rounded-full bg-[var(--brand-rose)] shadow-[0_0_12px_rgba(233,75,122,0.7)]"
                  />
                ) : null}
              </button>
            </li>
            {/* Perforation between frames. */}
            {i < STEPS.length - 1 ? (
              <li
                aria-hidden
                className="flex w-3 shrink-0 items-center justify-center text-[var(--brand-border)]"
              >
                <span className="inline-block h-1 w-1 rounded-full bg-current" />
                <span className="ml-0.5 inline-block h-1 w-1 rounded-full bg-current" />
                <span className="ml-0.5 inline-block h-1 w-1 rounded-full bg-current" />
              </li>
            ) : null}
          </Fragment>
        );
      })}
    </ol>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Step 1 — Metadata
// ────────────────────────────────────────────────────────────────────────────

function Step1Metadata({
  state,
  dispatch,
  onSlugManual,
}: {
  state: WizardState;
  dispatch: React.Dispatch<Action>;
  onSlugManual: () => void;
}) {
  const m = state.meta;
  return (
    <div className="space-y-6">
      <SectionHeader title="Series metadata" subtitle="The basics — bilingual." />

      <div className="grid gap-4 sm:grid-cols-2">
        <Field
          label="Title (EN)"
          required
          assist={
            <AiAssist
              field="series.title"
              context={{
                titleEn: m.titleEn,
                titleTh: m.titleTh,
                taglineEn: m.taglineEn,
                synopsisEn: m.synopsisEn,
                categories: m.categories,
                year: m.year,
              }}
              apply={(r) => dispatch({ type: "SET_META", patch: { titleEn: r.en, titleTh: r.th } })}
            />
          }
        >
          <Input
            value={m.titleEn}
            onChange={(e) => dispatch({ type: "SET_META", patch: { titleEn: e.target.value } })}
            placeholder="The Letters of Summer"
          />
        </Field>
        <Field label="Title (TH)">
          <Input
            value={m.titleTh}
            onChange={(e) => dispatch({ type: "SET_META", patch: { titleTh: e.target.value } })}
            placeholder="จดหมายฤดูร้อน"
          />
        </Field>
      </div>

      <Field label="Slug" hint="Auto-generated from the EN title. Edit if you need to.">
        <Input
          value={m.slug}
          onChange={(e) => {
            onSlugManual();
            dispatch({ type: "SET_META", patch: { slug: slugify(e.target.value) } });
          }}
          placeholder="letters-of-summer"
        />
      </Field>

      <div className="grid gap-4 sm:grid-cols-2">
        <Field
          label="Tagline (EN)"
          assist={
            <AiAssist
              field="series.tagline"
              context={{
                titleEn: m.titleEn,
                titleTh: m.titleTh,
                synopsisEn: m.synopsisEn,
                categories: m.categories,
              }}
              apply={(r) =>
                dispatch({ type: "SET_META", patch: { taglineEn: r.en, taglineTh: r.th } })
              }
            />
          }
        >
          <Input
            value={m.taglineEn}
            onChange={(e) => dispatch({ type: "SET_META", patch: { taglineEn: e.target.value } })}
            placeholder="One summer. Two boys. A thousand letters."
          />
        </Field>
        <Field label="Tagline (TH)">
          <Input
            value={m.taglineTh}
            onChange={(e) => dispatch({ type: "SET_META", patch: { taglineTh: e.target.value } })}
          />
        </Field>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <Field
          label="Synopsis (EN)"
          assist={
            <AiAssist
              field="series.synopsis"
              context={{
                titleEn: m.titleEn,
                titleTh: m.titleTh,
                taglineEn: m.taglineEn,
                categories: m.categories,
                cast: m.cast,
              }}
              apply={(r) =>
                dispatch({ type: "SET_META", patch: { synopsisEn: r.en, synopsisTh: r.th } })
              }
            />
          }
        >
          <Textarea
            value={m.synopsisEn}
            onChange={(v) => dispatch({ type: "SET_META", patch: { synopsisEn: v } })}
            rows={5}
            placeholder="A coming-of-age story about…"
          />
        </Field>
        <Field label="Synopsis (TH)">
          <Textarea
            value={m.synopsisTh}
            onChange={(v) => dispatch({ type: "SET_META", patch: { synopsisTh: v } })}
            rows={5}
          />
        </Field>
      </div>

      <div className="grid gap-4 sm:grid-cols-3">
        <UploadField
          mode="r2"
          kind="poster"
          label="Poster (9:16)"
          hint="JPG / PNG / WebP / AVIF, ≤ 10MB"
          accept="image/jpeg,image/png,image/webp,image/avif"
          previewUrl={m.posterUrl || null}
          previewAspect="9/16"
          apiFetch={adminApiFetch}
          onUploaded={({ url, key }) =>
            dispatch({
              type: "SET_META",
              patch: {
                posterUrl: url,
                posterR2Key: key,
                gallery: mergeGallery(m.gallery, { url, role: "poster", r2Key: key }),
              },
            })
          }
          onReset={() =>
            dispatch({ type: "SET_META", patch: { posterUrl: "", posterR2Key: undefined } })
          }
        />
        <UploadField
          mode="r2"
          kind="hero"
          label="Hero banner (16:9)"
          hint="Landscape banner for the detail page header"
          accept="image/jpeg,image/png,image/webp,image/avif"
          previewUrl={m.heroUrl || null}
          previewAspect="16/9"
          apiFetch={adminApiFetch}
          onUploaded={({ url, key }) =>
            dispatch({
              type: "SET_META",
              patch: {
                heroUrl: url,
                heroR2Key: key,
                gallery: mergeGallery(m.gallery, { url, role: "hero", r2Key: key }),
              },
            })
          }
          onReset={() =>
            dispatch({ type: "SET_META", patch: { heroUrl: "", heroR2Key: undefined } })
          }
        />
        <UploadField
          mode="stream"
          label="Series trailer"
          hint="Optional. MP4 ≤ 2GB"
          accept="video/mp4,video/quicktime,video/webm"
          name={m.titleEn ? `${m.titleEn} — trailer` : "Series trailer"}
          previewUrl={m.trailerStreamVideoId ? `stream://${m.trailerStreamVideoId}` : null}
          previewAspect="16/9"
          apiFetch={adminApiFetch}
          runTus={adminTusRunner}
          onUploaded={({ uid }) =>
            dispatch({ type: "SET_META", patch: { trailerStreamVideoId: uid } })
          }
          onReset={() => dispatch({ type: "SET_META", patch: { trailerStreamVideoId: "" } })}
        />
      </div>

      <Field label="Categories" hint="Pick from the seed list or add a custom tag.">
        <CategoryPicker
          selected={m.categories}
          onChange={(next) => dispatch({ type: "SET_META", patch: { categories: next } })}
        />
      </Field>

      <div className="grid gap-4 sm:grid-cols-3">
        <Field label="Age rating">
          <Select
            value={m.ageRating}
            onValueChange={(v) => dispatch({ type: "SET_META", patch: { ageRating: v } })}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {AGE_RATINGS.map((r) => (
                <SelectItem key={r} value={r}>
                  {r}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>
        <Field label="Year">
          <Input
            type="number"
            value={m.year}
            onChange={(e) =>
              dispatch({ type: "SET_META", patch: { year: Number(e.target.value) || 0 } })
            }
          />
        </Field>
        <Field label="Country">
          <Input
            value={m.country}
            onChange={(e) => dispatch({ type: "SET_META", patch: { country: e.target.value } })}
          />
        </Field>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Free episode count" hint="First N episodes shown free.">
          <Input
            type="number"
            min={0}
            value={m.freeEpisodeCount}
            onChange={(e) =>
              dispatch({
                type: "SET_META",
                patch: { freeEpisodeCount: Number(e.target.value) || 0 },
              })
            }
          />
        </Field>
        <Field label="Bundle price (THB)">
          <Input
            type="number"
            min={0}
            value={m.bundlePriceThb}
            onChange={(e) =>
              dispatch({
                type: "SET_META",
                patch: { bundlePriceThb: Number(e.target.value) || 0 },
              })
            }
          />
        </Field>
      </div>

      <SectionHeader
        title="Cast & crew"
        subtitle="Add the principal cast. Drag-drop a photo onto each card to upload."
      />
      <ul className="space-y-2">
        {m.cast.map((c, i) => (
          <li
            key={i}
            className="grid grid-cols-1 gap-3 rounded-2xl border border-[var(--brand-border)] bg-white p-3 sm:grid-cols-[140px_1fr_auto]"
          >
            <UploadField
              mode="r2"
              kind="still"
              label="Photo"
              hint="Square crop works best"
              accept="image/jpeg,image/png,image/webp,image/avif"
              previewUrl={c.photoUrl || null}
              previewAspect="1/1"
              apiFetch={adminApiFetch}
              onUploaded={({ url, key }) =>
                dispatch({
                  type: "UPDATE_CAST",
                  index: i,
                  patch: { photoUrl: url, photoR2Key: key },
                })
              }
              onReset={() =>
                dispatch({
                  type: "UPDATE_CAST",
                  index: i,
                  patch: { photoUrl: "", photoR2Key: undefined },
                })
              }
            />
            <div className="grid gap-2">
              <Input
                value={c.name}
                onChange={(e) =>
                  dispatch({ type: "UPDATE_CAST", index: i, patch: { name: e.target.value } })
                }
                placeholder="Name"
              />
              <div className="flex items-center gap-2">
                <Input
                  value={c.role}
                  onChange={(e) =>
                    dispatch({ type: "UPDATE_CAST", index: i, patch: { role: e.target.value } })
                  }
                  placeholder="Role / character"
                  className="flex-1"
                />
                <AiAssist
                  field="cast.role"
                  context={{
                    name: c.name,
                    titleEn: m.titleEn,
                    synopsisEn: m.synopsisEn,
                    categories: m.categories,
                  }}
                  apply={(r) => dispatch({ type: "UPDATE_CAST", index: i, patch: { role: r.en } })}
                />
              </div>
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => dispatch({ type: "REMOVE_CAST", index: i })}
              aria-label="Remove"
            >
              <Trash2 className="h-4 w-4" aria-hidden />
            </Button>
          </li>
        ))}
      </ul>
      <Button variant="outline" size="sm" onClick={() => dispatch({ type: "ADD_CAST" })}>
        <Plus className="h-4 w-4" aria-hidden /> Add cast member
      </Button>

      <SectionHeader title="Visibility" subtitle="Defaults for the publish step." />
      <div className="grid gap-3 sm:grid-cols-3">
        <ToggleRow
          label="Featured"
          hint="Surface on the landing hero row."
          checked={m.featured}
          onCheckedChange={(v) => dispatch({ type: "SET_META", patch: { featured: v } })}
        />
        <ToggleRow
          label="Published"
          hint="Visible to the public. Toggle off for soft-publish."
          checked={m.published}
          onCheckedChange={(v) => dispatch({ type: "SET_META", patch: { published: v } })}
        />
        <Field label="Scheduled release" hint="ISO datetime. A cron will flip Published on.">
          <Input
            type="datetime-local"
            value={m.scheduledPublishAt}
            onChange={(e) =>
              dispatch({ type: "SET_META", patch: { scheduledPublishAt: e.target.value } })
            }
          />
        </Field>
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Step 2 — Seasons
// ────────────────────────────────────────────────────────────────────────────

function Step2Seasons({
  state,
  dispatch,
}: {
  state: WizardState;
  dispatch: React.Dispatch<Action>;
}) {
  return (
    <div className="space-y-4">
      <SectionHeader
        title="Seasons"
        subtitle="One season is enough for most short-form series. Reorder with the up/down arrows."
      />
      <ul className="space-y-3">
        {state.seasons.map((season, i) => (
          <li
            key={season.localId}
            className="rounded-2xl border border-[var(--brand-border)] bg-white p-4"
          >
            <div className="mb-3 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Badge variant="muted">Season {season.seasonNumber}</Badge>
              </div>
              <div className="flex gap-1">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() =>
                    dispatch({ type: "MOVE_SEASON", localId: season.localId, direction: "up" })
                  }
                  disabled={i === 0}
                  aria-label="Move up"
                >
                  <ChevronUp className="h-4 w-4" aria-hidden />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() =>
                    dispatch({ type: "MOVE_SEASON", localId: season.localId, direction: "down" })
                  }
                  disabled={i === state.seasons.length - 1}
                  aria-label="Move down"
                >
                  <ChevronDown className="h-4 w-4" aria-hidden />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => dispatch({ type: "REMOVE_SEASON", localId: season.localId })}
                  disabled={state.seasons.length === 1}
                  aria-label="Remove season"
                >
                  <Trash2 className="h-4 w-4" aria-hidden />
                </Button>
              </div>
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <Field label="Title (EN)">
                <Input
                  value={season.titleEn}
                  onChange={(e) =>
                    dispatch({
                      type: "UPDATE_SEASON",
                      localId: season.localId,
                      patch: { titleEn: e.target.value },
                    })
                  }
                />
              </Field>
              <Field label="Title (TH)">
                <Input
                  value={season.titleTh}
                  onChange={(e) =>
                    dispatch({
                      type: "UPDATE_SEASON",
                      localId: season.localId,
                      patch: { titleTh: e.target.value },
                    })
                  }
                />
              </Field>
              <Field label="Synopsis (EN)">
                <Textarea
                  value={season.synopsisEn}
                  onChange={(v) =>
                    dispatch({
                      type: "UPDATE_SEASON",
                      localId: season.localId,
                      patch: { synopsisEn: v },
                    })
                  }
                  rows={3}
                />
              </Field>
              <Field label="Synopsis (TH)">
                <Textarea
                  value={season.synopsisTh}
                  onChange={(v) =>
                    dispatch({
                      type: "UPDATE_SEASON",
                      localId: season.localId,
                      patch: { synopsisTh: v },
                    })
                  }
                  rows={3}
                />
              </Field>
              <Field label="Release date">
                <Input
                  type="date"
                  value={season.releaseDate}
                  onChange={(e) =>
                    dispatch({
                      type: "UPDATE_SEASON",
                      localId: season.localId,
                      patch: { releaseDate: e.target.value },
                    })
                  }
                />
              </Field>
            </div>
            <div className="mt-3">
              <UploadField
                mode="stream"
                label="Season trailer"
                hint="Optional. MP4 ≤ 2GB"
                accept="video/mp4,video/quicktime,video/webm"
                name={
                  season.titleEn
                    ? `${season.titleEn} — trailer`
                    : `Season ${season.seasonNumber} — trailer`
                }
                previewUrl={
                  season.trailerStreamVideoId ? `stream://${season.trailerStreamVideoId}` : null
                }
                previewAspect="16/9"
                apiFetch={adminApiFetch}
                runTus={adminTusRunner}
                onUploaded={({ uid }) =>
                  dispatch({
                    type: "UPDATE_SEASON",
                    localId: season.localId,
                    patch: { trailerStreamVideoId: uid },
                  })
                }
                onReset={() =>
                  dispatch({
                    type: "UPDATE_SEASON",
                    localId: season.localId,
                    patch: { trailerStreamVideoId: "" },
                  })
                }
              />
            </div>
          </li>
        ))}
      </ul>
      <Button variant="outline" size="sm" onClick={() => dispatch({ type: "ADD_SEASON" })}>
        <Plus className="h-4 w-4" aria-hidden /> Add season
      </Button>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Step 3 — Episodes
// ────────────────────────────────────────────────────────────────────────────

function Step3Episodes({
  state,
  dispatch,
}: {
  state: WizardState;
  dispatch: React.Dispatch<Action>;
}) {
  return (
    <div className="space-y-6">
      <SectionHeader
        title="Episodes"
        subtitle="Group episodes under their season. Stream upload + auto-duration land in Phase B5."
      />
      <div className="space-y-6">
        {state.seasons.map((season) => {
          const eps = state.episodes
            .filter((e) => e.seasonLocalId === season.localId)
            .sort((a, b) => a.episodeNumber - b.episodeNumber);
          return (
            <div
              key={season.localId}
              className="rounded-2xl border border-[var(--brand-border)] bg-white p-4"
            >
              <div className="mb-3 flex items-center justify-between">
                <h3 className="font-heading font-semibold">
                  Season {season.seasonNumber}
                  {season.titleEn ? ` · ${season.titleEn}` : ""}
                </h3>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => dispatch({ type: "ADD_EPISODE", seasonLocalId: season.localId })}
                >
                  <Plus className="h-4 w-4" aria-hidden /> Add episode
                </Button>
              </div>
              {eps.length === 0 ? (
                <p className="text-sm text-[var(--brand-muted)]">No episodes yet.</p>
              ) : (
                <ul className="space-y-3">
                  {eps.map((ep, i) => (
                    <li key={ep.localId} className="rounded-xl bg-[var(--brand-pink-soft)]/40 p-3">
                      <div className="mb-2 flex items-center justify-between">
                        <Badge variant="muted">EP {ep.episodeNumber}</Badge>
                        <div className="flex gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() =>
                              dispatch({
                                type: "MOVE_EPISODE",
                                localId: ep.localId,
                                direction: "up",
                              })
                            }
                            disabled={i === 0}
                            aria-label="Move up"
                          >
                            <ChevronUp className="h-4 w-4" aria-hidden />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() =>
                              dispatch({
                                type: "MOVE_EPISODE",
                                localId: ep.localId,
                                direction: "down",
                              })
                            }
                            disabled={i === eps.length - 1}
                            aria-label="Move down"
                          >
                            <ChevronDown className="h-4 w-4" aria-hidden />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() =>
                              dispatch({ type: "REMOVE_EPISODE", localId: ep.localId })
                            }
                            aria-label="Remove episode"
                          >
                            <Trash2 className="h-4 w-4" aria-hidden />
                          </Button>
                        </div>
                      </div>
                      <div className="grid gap-2 sm:grid-cols-2">
                        <Field
                          label="Title (EN)"
                          assist={
                            <AiAssist
                              field="episode.title"
                              context={{
                                seriesTitleEn: state.meta.titleEn,
                                seriesTitleTh: state.meta.titleTh,
                                synopsisEn: state.meta.synopsisEn,
                                seasonNumber: season.seasonNumber,
                                episodeNumber: ep.episodeNumber,
                                descriptionEn: ep.descriptionEn,
                              }}
                              apply={(r) =>
                                dispatch({
                                  type: "UPDATE_EPISODE",
                                  localId: ep.localId,
                                  patch: { titleEn: r.en, titleTh: r.th },
                                })
                              }
                            />
                          }
                        >
                          <Input
                            value={ep.titleEn}
                            onChange={(e) =>
                              dispatch({
                                type: "UPDATE_EPISODE",
                                localId: ep.localId,
                                patch: { titleEn: e.target.value },
                              })
                            }
                          />
                        </Field>
                        <Field label="Title (TH)">
                          <Input
                            value={ep.titleTh}
                            onChange={(e) =>
                              dispatch({
                                type: "UPDATE_EPISODE",
                                localId: ep.localId,
                                patch: { titleTh: e.target.value },
                              })
                            }
                          />
                        </Field>
                        <Field
                          label="Description (EN)"
                          assist={
                            <AiAssist
                              field="episode.description"
                              context={{
                                seriesTitleEn: state.meta.titleEn,
                                synopsisEn: state.meta.synopsisEn,
                                seasonNumber: season.seasonNumber,
                                episodeNumber: ep.episodeNumber,
                                episodeTitleEn: ep.titleEn,
                                episodeTitleTh: ep.titleTh,
                              }}
                              apply={(r) =>
                                dispatch({
                                  type: "UPDATE_EPISODE",
                                  localId: ep.localId,
                                  patch: { descriptionEn: r.en, descriptionTh: r.th },
                                })
                              }
                            />
                          }
                        >
                          <Textarea
                            value={ep.descriptionEn}
                            onChange={(v) =>
                              dispatch({
                                type: "UPDATE_EPISODE",
                                localId: ep.localId,
                                patch: { descriptionEn: v },
                              })
                            }
                            rows={2}
                          />
                        </Field>
                        <Field label="Description (TH)">
                          <Textarea
                            value={ep.descriptionTh}
                            onChange={(v) =>
                              dispatch({
                                type: "UPDATE_EPISODE",
                                localId: ep.localId,
                                patch: { descriptionTh: v },
                              })
                            }
                            rows={2}
                          />
                        </Field>
                        <Field
                          label="Duration (seconds)"
                          hint="Auto-filled when the Stream video finishes encoding."
                        >
                          <Input
                            type="number"
                            min={0}
                            value={ep.durationSec}
                            onChange={(e) =>
                              dispatch({
                                type: "UPDATE_EPISODE",
                                localId: ep.localId,
                                patch: { durationSec: Number(e.target.value) || 0 },
                              })
                            }
                          />
                        </Field>
                        <div />
                        <UploadField
                          mode="stream"
                          label="Episode video"
                          hint="MP4 ≤ 2GB. Resumable — close the tab and come back."
                          accept="video/mp4,video/quicktime,video/webm"
                          name={
                            ep.titleEn
                              ? `${state.meta.titleEn || "series"} S${season.seasonNumber}E${ep.episodeNumber} — ${ep.titleEn}`
                              : `${state.meta.titleEn || "series"} S${season.seasonNumber}E${ep.episodeNumber}`
                          }
                          previewUrl={ep.streamVideoId ? `stream://${ep.streamVideoId}` : null}
                          previewAspect="16/9"
                          apiFetch={adminApiFetch}
                          runTus={adminTusRunner}
                          onUploaded={({ uid, meta }) => {
                            const duration =
                              meta && typeof meta.durationSec === "number" && meta.durationSec > 0
                                ? meta.durationSec
                                : ep.durationSec;
                            dispatch({
                              type: "UPDATE_EPISODE",
                              localId: ep.localId,
                              patch: { streamVideoId: uid, durationSec: duration },
                            });
                          }}
                          onReset={() =>
                            dispatch({
                              type: "UPDATE_EPISODE",
                              localId: ep.localId,
                              patch: { streamVideoId: "" },
                            })
                          }
                        />
                        <UploadField
                          mode="r2"
                          kind="thumbnail"
                          label="Thumbnail"
                          hint="16:9 still, JPG / PNG / WebP"
                          accept="image/jpeg,image/png,image/webp,image/avif"
                          previewUrl={ep.thumbnailUrl || null}
                          previewAspect="16/9"
                          apiFetch={adminApiFetch}
                          onUploaded={({ url, key }) =>
                            dispatch({
                              type: "UPDATE_EPISODE",
                              localId: ep.localId,
                              patch: { thumbnailUrl: url, thumbnailR2Key: key },
                            })
                          }
                          onReset={() =>
                            dispatch({
                              type: "UPDATE_EPISODE",
                              localId: ep.localId,
                              patch: { thumbnailUrl: "", thumbnailR2Key: undefined },
                            })
                          }
                        />
                        <Field label="Price (THB)">
                          <Input
                            type="number"
                            min={0}
                            value={ep.episodePriceThb}
                            onChange={(e) =>
                              dispatch({
                                type: "UPDATE_EPISODE",
                                localId: ep.localId,
                                patch: { episodePriceThb: Number(e.target.value) || 0 },
                              })
                            }
                          />
                        </Field>
                        <Field
                          label="Free/paid override"
                          hint="Default respects the series free-episode-count."
                        >
                          <Select
                            value={ep.freeMode}
                            onValueChange={(v) =>
                              dispatch({
                                type: "UPDATE_EPISODE",
                                localId: ep.localId,
                                patch: { freeMode: v as EpisodeRow["freeMode"] },
                              })
                            }
                          >
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="default">Default</SelectItem>
                              <SelectItem value="free">Always free</SelectItem>
                              <SelectItem value="paid">Always paid</SelectItem>
                            </SelectContent>
                          </Select>
                        </Field>
                        <Field label="Released at">
                          <Input
                            type="datetime-local"
                            value={ep.releasedAt}
                            onChange={(e) =>
                              dispatch({
                                type: "UPDATE_EPISODE",
                                localId: ep.localId,
                                patch: { releasedAt: e.target.value },
                              })
                            }
                          />
                        </Field>
                      </div>
                      <EpisodeSubtitles ep={ep} dispatch={dispatch} />
                    </li>
                  ))}
                </ul>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// Per-episode subtitle tracks. Each upload posts a VTT/SRT to R2 and tags
// it with a BCP-47 language code; the player reads `episodes.subtitles[]`
// as its sidecar caption list. Re-uploading a language replaces the row
// (the reducer dedupes by language).
const SUBTITLE_LANGS: Array<{ code: string; label: string }> = [
  { code: "en", label: "English" },
  { code: "th", label: "ไทย (Thai)" },
];

function EpisodeSubtitles({ ep, dispatch }: { ep: EpisodeRow; dispatch: React.Dispatch<Action> }) {
  return (
    <div className="mt-3 rounded-xl border border-[var(--brand-border)] bg-white p-3">
      <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-[var(--brand-muted)]">
        Subtitles
      </p>
      <div className="grid gap-3 sm:grid-cols-2">
        {SUBTITLE_LANGS.map((lang) => {
          const existing = ep.subtitles.find((s) => s.language === lang.code);
          return (
            <UploadField
              key={lang.code}
              mode="r2"
              kind="subtitle"
              label={`${lang.label} (.vtt / .srt)`}
              hint="≤ 256KB"
              accept="text/vtt,application/x-subrip,.vtt,.srt"
              previewUrl={existing?.vttUrl ?? null}
              previewAspect="16/3"
              apiFetch={adminApiFetch}
              onUploaded={({ url, key }) =>
                dispatch({
                  type: "ADD_SUBTITLE",
                  episodeLocalId: ep.localId,
                  row: { language: lang.code, label: lang.label, vttUrl: url, r2Key: key },
                })
              }
              onReset={() =>
                dispatch({
                  type: "REMOVE_SUBTITLE",
                  episodeLocalId: ep.localId,
                  language: lang.code,
                })
              }
            />
          );
        })}
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Step 4 — Bulk import (stretch)
// ────────────────────────────────────────────────────────────────────────────

interface BulkImportPayload {
  meta?: Partial<SeriesMeta>;
  seasons?: Array<{
    seasonNumber?: number;
    titleEn?: string;
    titleTh?: string;
    synopsisEn?: string;
    synopsisTh?: string;
    releaseDate?: string;
    episodes?: Array<{
      episodeNumber?: number;
      titleEn?: string;
      titleTh?: string;
      descriptionEn?: string;
      descriptionTh?: string;
      durationSec?: number;
      thumbnailUrl?: string;
      episodePriceThb?: number;
      releasedAt?: string;
    }>;
  }>;
}

function Step4Import({
  state,
  dispatch,
}: {
  state: WizardState;
  dispatch: React.Dispatch<Action>;
}) {
  const [raw, setRaw] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [slugConflict, setSlugConflict] = useState<string | null>(null);

  const apply = async () => {
    setError(null);
    setSlugConflict(null);
    try {
      const parsed = JSON.parse(raw) as BulkImportPayload;
      // Map seasons/episodes into local-id-tagged rows.
      const newSeasons: SeasonRow[] = (parsed.seasons ?? []).map((s, i) => ({
        localId: newLocalId("season"),
        seasonNumber: s.seasonNumber ?? i + 1,
        titleEn: s.titleEn ?? "",
        titleTh: s.titleTh ?? "",
        synopsisEn: s.synopsisEn ?? "",
        synopsisTh: s.synopsisTh ?? "",
        trailerStreamVideoId: "",
        releaseDate: s.releaseDate ?? "",
      }));
      const newEpisodes: EpisodeRow[] = (parsed.seasons ?? []).flatMap((s, si) => {
        const parent = newSeasons[si];
        if (!parent) return [];
        return (s.episodes ?? []).map((e, ei) => ({
          localId: newLocalId("ep"),
          seasonLocalId: parent.localId,
          episodeNumber: e.episodeNumber ?? ei + 1,
          titleEn: e.titleEn ?? "",
          titleTh: e.titleTh ?? "",
          descriptionEn: e.descriptionEn ?? "",
          descriptionTh: e.descriptionTh ?? "",
          streamVideoId: "",
          durationSec: e.durationSec ?? 0,
          thumbnailUrl: e.thumbnailUrl ?? "",
          subtitles: [],
          episodePriceThb: e.episodePriceThb ?? 9,
          freeMode: "default" as const,
          releasedAt: e.releasedAt ?? "",
        }));
      });

      // Slug-conflict check against existing series.
      if (parsed.meta?.slug || state.meta.slug) {
        const slugToCheck = parsed.meta?.slug ?? state.meta.slug;
        try {
          const data = await api.get<{ items: Array<{ _id: string; slug: string }> }>(
            "/api/admin/series",
          );
          if (data.items.some((s) => s.slug === slugToCheck && s._id !== state.meta._id)) {
            setSlugConflict(slugToCheck);
            return;
          }
        } catch {
          // ignore — we'll still try to import
        }
      }

      dispatch({
        type: "BULK_IMPORT",
        meta: parsed.meta,
        seasons: newSeasons.length > 0 ? newSeasons : undefined,
        episodes: newEpisodes.length > 0 ? newEpisodes : undefined,
      });
      toast.success("Imported");
    } catch (err) {
      setError((err as Error).message || "Invalid JSON");
    }
  };

  return (
    <div className="space-y-4">
      <SectionHeader
        title="Bulk import (optional)"
        subtitle="Paste a JSON manifest to pre-fill steps 1–3. Skip this if you'd rather fill the form by hand."
      />
      <details className="rounded-2xl border border-[var(--brand-border)] bg-white p-4 text-sm">
        <summary className="cursor-pointer font-semibold">Expected shape</summary>
        <pre className="mt-2 max-h-60 overflow-auto rounded-lg bg-[var(--brand-pink-soft)]/50 p-3 text-[11px] leading-relaxed">
          {`{
  "meta": {
    "slug": "summer-storms",
    "titleEn": "Summer Storms",
    "titleTh": "พายุฤดูร้อน",
    "synopsisEn": "...",
    "categories": ["bl","romance"]
  },
  "seasons": [
    {
      "seasonNumber": 1,
      "titleEn": "Season 1",
      "releaseDate": "2026-06-01",
      "episodes": [
        { "episodeNumber": 1, "titleEn": "Letters", "durationSec": 480 }
      ]
    }
  ]
}`}
        </pre>
      </details>
      <Textarea
        value={raw}
        onChange={setRaw}
        rows={12}
        placeholder='{ "meta": { "titleEn": "..." }, "seasons": [ ... ] }'
      />
      {error ? <p className="text-sm text-[var(--brand-rose)]">{error}</p> : null}
      {slugConflict ? (
        <div className="rounded-xl border border-[var(--brand-rose)] bg-[var(--brand-pink-soft)] p-3 text-sm">
          Slug <code className="font-mono">{slugConflict}</code> is already in use. Pick a different
          one in the manifest's <code className="font-mono">meta.slug</code>.
        </div>
      ) : null}
      <Button variant="primary" onClick={apply} disabled={!raw.trim()}>
        Apply import
      </Button>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Step 5 — Premiere preview
// ────────────────────────────────────────────────────────────────────────────

function Step5Preview({ state }: { state: WizardState }) {
  const m = state.meta;
  const totalEps = state.episodes.length;
  const sortedSeasons = state.seasons.slice().sort((a, b) => a.seasonNumber - b.seasonNumber);

  return (
    <div className="space-y-6">
      <SectionHeader title="Premiere preview" subtitle="This is how your audience will see it." />

      {/* Card preview row — three at-a-glance cards. */}
      <div className="rounded-3xl border border-[var(--brand-border)] bg-white p-6">
        <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-[var(--brand-muted)]">
          On a discovery row
        </p>
        <div className="flex gap-3 overflow-x-auto pb-2">
          {[1, 2, 3].map((i) => (
            <div key={i} className="w-[160px] shrink-0">
              <VerticalPosterCard
                href="#"
                titleEn={m.titleEn || "Untitled"}
                titleTh={m.titleTh || m.titleEn || "Untitled"}
                taglineEn={m.taglineEn || null}
                taglineTh={m.taglineTh || null}
                posterUrl={m.posterUrl || placeholderPoster()}
                freeEpisodeCount={m.freeEpisodeCount}
                rank={i === 1 ? 1 : undefined}
                locale="en"
              />
            </div>
          ))}
        </div>
      </div>

      {/* Detail page preview — mirrors apps/web series-detail.tsx layout. */}
      <div className="overflow-hidden rounded-3xl border border-[var(--brand-border)] bg-white">
        <section className="relative">
          <div
            aria-hidden
            className="absolute inset-0 bg-cover bg-center"
            style={{
              backgroundImage: `url(${m.posterUrl || placeholderPoster()})`,
            }}
          />
          <div aria-hidden className="absolute inset-0 backdrop-blur-2xl backdrop-saturate-150" />
          <div
            aria-hidden
            className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/55 to-white"
          />
          <div className="relative grid grid-cols-[140px_1fr] gap-5 p-6">
            <div className="aspect-[9/16] overflow-hidden rounded-xl shadow-[0_20px_60px_-20px_rgba(0,0,0,0.6)]">
              <img
                src={m.posterUrl || placeholderPoster()}
                alt={m.titleEn}
                className="h-full w-full object-cover"
              />
            </div>
            <div className="space-y-3 text-white">
              <h1 className="font-display text-2xl leading-tight sm:text-3xl">
                {m.titleEn || "Untitled"}
              </h1>
              {m.taglineEn ? <p className="text-sm text-white/85">{m.taglineEn}</p> : null}
              <div className="flex flex-wrap gap-1.5 text-[11px]">
                {m.year ? (
                  <span className="rounded-full bg-white/15 px-2 py-0.5 backdrop-blur">
                    {m.year}
                  </span>
                ) : null}
                {m.ageRating ? (
                  <span className="rounded-full bg-white/15 px-2 py-0.5 backdrop-blur">
                    {m.ageRating}
                  </span>
                ) : null}
                <span className="rounded-full bg-white/15 px-2 py-0.5 backdrop-blur">
                  {totalEps} episodes
                </span>
                {m.categories.slice(0, 3).map((c) => (
                  <span
                    key={c}
                    className="rounded-full border border-white/30 px-2 py-0.5 capitalize backdrop-blur"
                  >
                    {c.replace(/-/g, " ")}
                  </span>
                ))}
              </div>
              <div className="flex gap-2 pt-1">
                <span className="inline-flex h-9 items-center rounded-full bg-white px-4 text-xs font-semibold text-[var(--brand-ink)]">
                  Watch first episode free
                </span>
                <span className="inline-flex h-9 items-center rounded-full border border-white/60 px-4 text-xs font-semibold text-white">
                  + Watchlist
                </span>
              </div>
              <p className="pt-1 text-[11px] text-white/75">
                First {m.freeEpisodeCount} episodes free · Unlock the bundle for ฿{m.bundlePriceThb}
              </p>
            </div>
          </div>
        </section>

        {/* Synopsis */}
        <section className="space-y-2 px-6 pt-6">
          <h2 className="font-heading text-lg font-bold">Synopsis</h2>
          <p className="whitespace-pre-line text-sm leading-relaxed text-[var(--brand-ink)]/90">
            {m.synopsisEn || (
              <span className="italic text-[var(--brand-muted)]">Add a synopsis in step 1.</span>
            )}
          </p>
        </section>

        {/* Episodes by season */}
        <section className="space-y-3 px-6 pt-6">
          <h2 className="font-heading text-lg font-bold">Episodes</h2>
          {sortedSeasons.map((season) => {
            const eps = state.episodes
              .filter((e) => e.seasonLocalId === season.localId)
              .sort((a, b) => a.episodeNumber - b.episodeNumber);
            return (
              <div key={season.localId} className="space-y-2">
                <p className="text-xs font-semibold uppercase tracking-wide text-[var(--brand-muted)]">
                  Season {season.seasonNumber}
                  {season.titleEn ? ` · ${season.titleEn}` : ""}
                </p>
                {eps.length === 0 ? (
                  <p className="text-xs text-[var(--brand-muted)]">No episodes yet.</p>
                ) : (
                  <ul className="space-y-1.5">
                    {eps.map((ep) => {
                      const free =
                        ep.freeMode === "free" ||
                        (ep.freeMode === "default" && ep.episodeNumber <= m.freeEpisodeCount);
                      return (
                        <li
                          key={ep.localId}
                          className="flex items-center gap-3 rounded-xl bg-[var(--brand-pink-soft)]/50 p-2.5"
                        >
                          <span className="font-mono text-xs text-[var(--brand-muted)]">
                            EP{ep.episodeNumber}
                          </span>
                          <span className="flex-1 truncate text-sm font-medium">
                            {ep.titleEn || `Episode ${ep.episodeNumber}`}
                          </span>
                          {free ? (
                            <Badge variant="free">Free</Badge>
                          ) : (
                            <Badge variant="locked">฿{ep.episodePriceThb}</Badge>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            );
          })}
        </section>

        {/* Cast */}
        {m.cast.filter((c) => c.name.trim()).length > 0 ? (
          <section className="space-y-3 px-6 pb-6 pt-6">
            <h2 className="font-heading text-lg font-bold">Cast</h2>
            <ul className="grid grid-cols-2 gap-2 sm:grid-cols-3">
              {m.cast
                .filter((c) => c.name.trim())
                .map((p, i) => (
                  <li key={i} className="rounded-xl bg-[var(--brand-pink-soft)] px-3 py-2">
                    <p className="text-sm font-semibold">{p.name}</p>
                    {p.role ? <p className="text-xs text-[var(--brand-muted)]">{p.role}</p> : null}
                  </li>
                ))}
            </ul>
          </section>
        ) : (
          <div className="pb-6" />
        )}
      </div>
    </div>
  );
}

function placeholderPoster(): string {
  return "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 9 16'%3E%3Crect width='9' height='16' fill='%23f7c8d5'/%3E%3Cg fill='%23e94b7a' opacity='0.45'%3E%3Ccircle cx='4.5' cy='6' r='1.6'/%3E%3Crect x='2' y='9' width='5' height='0.4' rx='0.2'/%3E%3Crect x='2.4' y='10' width='4.2' height='0.3' rx='0.15'/%3E%3C/g%3E%3C/svg%3E";
}

// ────────────────────────────────────────────────────────────────────────────
// Atoms
// ────────────────────────────────────────────────────────────────────────────

function SectionHeader({ title, subtitle }: { title: string; subtitle?: string }) {
  return (
    <div className="space-y-1">
      <h2 className="font-heading text-xl font-bold">{title}</h2>
      {subtitle ? <p className="text-sm text-[var(--brand-muted)]">{subtitle}</p> : null}
    </div>
  );
}

/**
 * Wraps `<AiAssistButton />` with the admin api client. The `apply`
 * callback receives the bilingual {en, th} result and is expected to
 * dispatch the appropriate state update — for one locale or both.
 */
function AiAssist({
  field,
  context,
  apply,
}: {
  field: AiFieldKind;
  context: AiFieldContext;
  apply: (result: AiAssistResult) => void;
}) {
  return (
    <AiAssistButton
      field={field}
      context={context}
      request={async ({ field: f, context: ctx }) =>
        api.post<AiAssistResult>("/api/admin/ai/generate-field", { field: f, context: ctx })
      }
      onResult={apply}
    />
  );
}

function Field({
  label,
  hint,
  required,
  children,
  assist,
}: {
  label: string;
  hint?: string;
  required?: boolean;
  children: React.ReactNode;
  /** Slot for the ✨ "generate with AI" button next to the label. */
  assist?: React.ReactNode;
}) {
  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between gap-2">
        <Label className="flex items-center gap-1">
          {label}
          {required ? <span className="text-[var(--brand-rose)]">*</span> : null}
        </Label>
        {assist}
      </div>
      {children}
      {hint ? <p className="text-[11px] text-[var(--brand-muted)]">{hint}</p> : null}
    </div>
  );
}

function ToggleRow({
  label,
  hint,
  checked,
  onCheckedChange,
}: {
  label: string;
  hint?: string;
  checked: boolean;
  onCheckedChange: (v: boolean) => void;
}) {
  return (
    <div className="flex items-start justify-between gap-2 rounded-2xl border border-[var(--brand-border)] bg-white px-3 py-2.5">
      <div>
        <p className="text-sm font-medium">{label}</p>
        {hint ? <p className="text-[11px] text-[var(--brand-muted)]">{hint}</p> : null}
      </div>
      <Switch checked={checked} onCheckedChange={onCheckedChange} />
    </div>
  );
}

function Textarea({
  value,
  onChange,
  rows = 4,
  placeholder,
}: {
  value: string;
  onChange: (v: string) => void;
  rows?: number;
  placeholder?: string;
}) {
  return (
    <textarea
      value={value}
      onChange={(e) => onChange(e.target.value)}
      rows={rows}
      placeholder={placeholder}
      className="w-full rounded-2xl border border-[var(--brand-border)] bg-[var(--brand-paper)] p-3 text-sm focus:outline-none focus:ring-2 focus:ring-brand-rose"
    />
  );
}

function CategoryPicker({
  selected,
  onChange,
}: {
  selected: string[];
  onChange: (next: string[]) => void;
}) {
  const [draft, setDraft] = useState("");
  const all = useMemo(() => {
    const set = new Set(SEEDED_CATEGORIES);
    for (const s of selected) set.add(s);
    return Array.from(set);
  }, [selected]);

  const toggle = (slug: string) => {
    if (selected.includes(slug)) onChange(selected.filter((c) => c !== slug));
    else onChange([...selected, slug]);
  };

  const addCustom = () => {
    const v = slugify(draft);
    if (!v) return;
    if (!selected.includes(v)) onChange([...selected, v]);
    setDraft("");
  };

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-1.5">
        {all.map((slug) => {
          const on = selected.includes(slug);
          return (
            <button
              key={slug}
              type="button"
              onClick={() => toggle(slug)}
              className={cn(
                "inline-flex items-center gap-1 rounded-full border px-3 py-1 text-xs font-medium transition-colors",
                on
                  ? "border-[var(--brand-rose)] bg-[var(--brand-rose)] text-white"
                  : "border-[var(--brand-border)] bg-white text-[var(--brand-ink)] hover:border-[var(--brand-pink)]",
              )}
            >
              {on ? <Check className="h-3 w-3" aria-hidden /> : null}
              {slug}
              {on && !SEEDED_CATEGORIES.includes(slug) ? (
                <X
                  className="h-3 w-3 opacity-80"
                  aria-hidden
                  onClick={(e) => {
                    e.stopPropagation();
                    onChange(selected.filter((c) => c !== slug));
                  }}
                />
              ) : null}
            </button>
          );
        })}
      </div>
      <div className="flex items-center gap-2">
        <Input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              addCustom();
            }
          }}
          placeholder="Add custom tag…"
          className="h-9 max-w-xs text-xs"
        />
        <Button variant="ghost" size="sm" onClick={addCustom} disabled={!draft.trim()}>
          Add
        </Button>
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Server → wizard hydration helpers
// ────────────────────────────────────────────────────────────────────────────

interface ServerGalleryImage {
  url: string;
  role: GalleryImage["role"];
  r2Key?: string;
  alt?: string;
}

interface ServerSeries {
  _id: string;
  slug: string;
  titleEn: string;
  titleTh: string;
  taglineEn?: string;
  taglineTh?: string;
  synopsisEn: string;
  synopsisTh: string;
  posterUrl: string;
  gallery?: ServerGalleryImage[];
  trailerStreamVideoId?: string;
  categories: string[];
  ageRating?: string;
  year?: number;
  country?: string;
  cast?: Array<{ name: string; role?: string; photoUrl?: string }>;
  featured: boolean;
  published: boolean;
  freeEpisodeCount?: number;
  bundlePriceThb?: number | null;
  scheduledPublishAt?: string | null;
}

interface ServerSeason {
  _id: string;
  seasonNumber: number;
  titleEn?: string;
  titleTh?: string;
  synopsisEn?: string;
  synopsisTh?: string;
  trailerStreamVideoId?: string;
  releaseDate?: string;
}

interface ServerSubtitle {
  language: string;
  label: string;
  vttUrl: string;
  default?: boolean;
}

interface ServerEpisode {
  _id: string;
  seasonNumber: number;
  episodeNumber: number;
  titleEn: string;
  titleTh: string;
  descriptionEn?: string;
  descriptionTh?: string;
  streamVideoId?: string;
  durationSec: number;
  thumbnailUrl: string;
  subtitles?: ServerSubtitle[];
  episodePriceThb?: number | null;
  isFreeOverride?: boolean | null;
  releasedAt?: string;
}

interface ServerDraft {
  _id: string;
  updatedAt: string;
  finalized: boolean;
  payload: unknown;
}

function hydrateFromServer(
  s: ServerSeries,
  seasonRows: ServerSeason[],
  epRows: ServerEpisode[],
): WizardState {
  const seasons: SeasonRow[] = (
    seasonRows.length > 0
      ? seasonRows
      : ([{ _id: `${s._id}-s1`, seasonNumber: 1 }] as ServerSeason[])
  ).map((row) => ({
    localId: newLocalId("season"),
    _id: row._id,
    seasonNumber: row.seasonNumber,
    titleEn: row.titleEn ?? "",
    titleTh: row.titleTh ?? "",
    synopsisEn: row.synopsisEn ?? "",
    synopsisTh: row.synopsisTh ?? "",
    trailerStreamVideoId: row.trailerStreamVideoId ?? "",
    releaseDate: row.releaseDate ? row.releaseDate.slice(0, 10) : "",
  }));
  const seasonByNumber = new Map(seasons.map((sn) => [sn.seasonNumber, sn.localId]));
  const fallbackSeasonId = seasons[0]?.localId ?? newLocalId("season");
  const episodes: EpisodeRow[] = epRows.map((row) => ({
    localId: newLocalId("ep"),
    _id: row._id,
    seasonLocalId: seasonByNumber.get(row.seasonNumber) ?? fallbackSeasonId,
    episodeNumber: row.episodeNumber,
    titleEn: row.titleEn,
    titleTh: row.titleTh,
    descriptionEn: row.descriptionEn ?? "",
    descriptionTh: row.descriptionTh ?? "",
    streamVideoId: row.streamVideoId ?? "",
    durationSec: row.durationSec,
    thumbnailUrl: row.thumbnailUrl,
    subtitles: (row.subtitles ?? []).map((sub) => ({
      language: sub.language,
      label: sub.label,
      vttUrl: sub.vttUrl,
    })),
    episodePriceThb: row.episodePriceThb ?? 9,
    freeMode:
      row.isFreeOverride === true ? "free" : row.isFreeOverride === false ? "paid" : "default",
    releasedAt: row.releasedAt ? row.releasedAt.slice(0, 16) : "",
  }));
  // Hydrate hero from the gallery if present; fall back to empty string and
  // let the admin upload one in step 1.
  const heroGallery = (s.gallery ?? []).find((g) => g.role === "hero");
  return {
    meta: {
      _id: s._id,
      slug: s.slug,
      titleEn: s.titleEn,
      titleTh: s.titleTh,
      taglineEn: s.taglineEn ?? "",
      taglineTh: s.taglineTh ?? "",
      synopsisEn: s.synopsisEn,
      synopsisTh: s.synopsisTh,
      posterUrl: s.posterUrl,
      heroUrl: heroGallery?.url ?? "",
      heroR2Key: heroGallery?.r2Key,
      trailerStreamVideoId: s.trailerStreamVideoId ?? "",
      gallery: (s.gallery ?? []).map((g) => ({
        url: g.url,
        role: g.role,
        r2Key: g.r2Key,
        alt: g.alt,
      })),
      categories: s.categories,
      ageRating: s.ageRating ?? "15+",
      year: s.year ?? new Date().getFullYear(),
      country: s.country ?? "Thailand",
      cast: (s.cast ?? []).map((c) => ({
        name: c.name,
        role: c.role ?? "",
        photoUrl: c.photoUrl ?? "",
      })),
      featured: s.featured,
      published: s.published,
      freeEpisodeCount: s.freeEpisodeCount ?? 3,
      bundlePriceThb: s.bundlePriceThb ?? 89,
      scheduledPublishAt: s.scheduledPublishAt ? s.scheduledPublishAt.slice(0, 16) : "",
    },
    seasons,
    episodes,
    step: 1,
  };
}
